import styled from "styled-components";
import { rawColors } from "../../../design-system/tokens/colors";
import { spacing } from "../../../design-system/tokens/spacing";
import { borderRadius } from "../../../design-system/tokens/borders";
import { typographyMixin } from "../../../design-system/tokens/typography";
import { duration, easing } from "../../../design-system/tokens/transitions";

export const ModalBody = styled.div`
  padding: ${spacing[6]};
  display: flex;
  flex-direction: column;
  gap: ${spacing[4]};
`;

export const ModalTitle = styled.h2`
  margin: 0;
  ${typographyMixin("subtitle", 1)}
  color: ${rawColors.text.highEmphasis};
`;

export const ModalDescription = styled.p`
  margin: 0;
  ${typographyMixin("body", 3)}
  color: ${rawColors.text.mediumEmphasis};
`;

export const InfoBox = styled.div<{ $variant: "info" | "warning" }>`
  background-color: ${({ $variant }) =>
    $variant === "warning"
      ? rawColors.accent.yellowLight
      : rawColors.primary[50]};
  border-radius: ${borderRadius.md};
  padding: ${spacing[3]};
  display: flex;
  gap: ${spacing[2]};
`;

export const InfoIcon = styled.div<{ $variant: "info" | "warning" }>`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 1.25rem;
    height: 1.25rem;
    color: ${({ $variant }) =>
      $variant === "warning"
        ? rawColors.accent.yellow500
        : rawColors.primary[500]};
  }
`;

export const InfoText = styled.p`
  margin: 0;
  ${typographyMixin("caption", 4)}
  color: ${rawColors.text.mediumEmphasis};
`;

export const ModalFooter = styled.div`
  border-top: 1px solid ${rawColors.stroke[200]};
  padding: ${spacing[6]};
  display: flex;
  gap: ${spacing[3]};
  justify-content: flex-end;
`;

// Figma 2572-180181 — white bg + structural/200 border, medium-emphasis text.
export const CancelButton = styled.button`
  padding: ${spacing[2]} ${spacing[4]};
  min-width: 10rem;
  justify-content: center;
  border-radius: ${borderRadius.md};
  border: 1px solid ${rawColors.structural[200]};
  background-color: ${rawColors.structural.white};
  color: ${rawColors.text.mediumEmphasis};
  ${typographyMixin("body", 3)}
  font-weight: 500;
  cursor: pointer;
  transition: background-color ${duration.fast} ${easing.smooth};

  &:hover:not(:disabled) {
    background-color: ${rawColors.structural[100]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Figma 2572-180181 — both primary and danger use primary/900 (#041840) dark navy.
// The design uses a single dark button; "danger" is a semantic hint only.
export const ConfirmButton = styled.button<{ $variant?: "primary" | "danger" }>`
  padding: ${spacing[2]} ${spacing[4]};
  min-width: 10rem;
  justify-content: center;
  border-radius: ${borderRadius.md};
  border: none;
  background-color: ${rawColors.primary[900]};
  color: ${rawColors.text.white};
  ${typographyMixin("body", 3)}
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  transition: background-color ${duration.fast} ${easing.smooth};

  &:hover:not(:disabled) {
    background-color: ${rawColors.primary[800]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 1rem;
    height: 1rem;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;
